/**
 * Extract YouTube video ID from common URL shapes.
 * @param {string | null | undefined} url
 * @returns {string | null}
 */
export function extractYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();

  const patterns = [
    /(?:youtube\.com\/watch\?)(?:[^#]*&)?v=([a-zA-Z0-9_-]{11})(?:[&#[\s]|$)/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})(?:[?&#]|$)/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})(?:[?&#]|$)/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})(?:[?&#]|$)/,
    /youtube\.com\/live\/([a-zA-Z0-9_-]{11})(?:[?&#]|$)/,
    /m\.youtube\.com\/watch\?(?:[^#]*&)?v=([a-zA-Z0-9_-]{11})(?:[&#[\s]|$)/,
  ];

  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m) return m[1];
  }
  return null;
}

/**
 * Build iframe src for YouTube embed.
 * By default uses a minimal param set because origin / enablejsapi can cause
 * intermittent error 152 on localhost and unusual hosts.
 * @param {string | null | undefined} pageUrl
 * @param {{ startSeconds?: number, autoplay?: boolean, enableJsApi?: boolean, includeOrigin?: boolean }} [options]
 * @returns {string | null}
 */
export function buildYouTubeEmbedSrcFromPageUrl(pageUrl, options = {}) {
  const id = extractYouTubeVideoId(pageUrl);
  if (!id) return null;

  const params = new URLSearchParams({
    playsinline: '1',
    rel: '0',
    modestbranding: '1',
    autoplay: options.autoplay ? '1' : '0',
    controls: '1',
  });

  if (options.startSeconds != null && Number(options.startSeconds) >= 0) {
    params.set('start', String(Math.floor(Number(options.startSeconds))));
  }

  if (options.includeOrigin === true && typeof window !== 'undefined' && window.location?.origin) {
    params.set('origin', window.location.origin);
  }

  if (options.enableJsApi === true) {
    params.set('enablejsapi', '1');
  }

  return `https://www.youtube.com/embed/${encodeURIComponent(id)}?${params.toString()}`;
}

/** @type {Promise<void> | null} */
let youtubeIframeApiPromise = null;

/**
 * Loads https://www.youtube.com/iframe_api once (YouTube IFrame API for YT.Player).
 * @returns {Promise<void>}
 */
export function loadYouTubeIframeApi() {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (window.YT && window.YT.Player) {
    return Promise.resolve();
  }
  if (!youtubeIframeApiPromise) {
    youtubeIframeApiPromise = new Promise((resolve) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === 'function') {
          previous();
        }
        resolve();
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
    });
  }
  return youtubeIframeApiPromise;
}
